import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Bell,
  ChevronDown,
  Command,
  LogOut,
  User as UserIcon
} from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface HeaderProps {
  toggleSidebar: () => void;
  isMobile: boolean;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar, isMobile }) => {
  const { user, logout, loading, environment, switchEnvironment } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionTime, setSessionTime] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout', error);
    }
  };

  const envStyles: Record<string, string> = {
    Development: 'bg-green-100 text-green-800 border-green-200',
    QA: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Production: 'bg-red-100 text-red-800 border-red-200 animate-pulse-slow ring-1 ring-red-300'
  };

  // Session Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  const formatSessionTime = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  // Command Palette Logic
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setIsSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchQuery('');
        searchInputRef.current?.blur();
        setIsProfileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const quickActions = [
    { label: 'Create new Workshop', path: '/create-workshop', keywords: ['work', 'create', 'shop'] },
    { label: 'Go to Workshop list', path: '/workshops', keywords: ['work', 'list', 'shop'] },
    { label: 'Manage Users', path: '/users', keywords: ['user', 'admin'] },
    { label: 'View Audit Logs', path: '/audit-logs', keywords: ['log', 'audit'] },
  ];

  const filteredActions = searchQuery
    ? quickActions.filter(action =>
      action.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.keywords.some(k => k.includes(searchQuery.toLowerCase()))
    )
    : [];

  const handleActionClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  // Safe user display name logic
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const displayEmail = user?.email || '';
  const initial = displayName.charAt(0).toUpperCase();

  const getEnvColor = (env: string) => {
    if (env === 'Production') return 'bg-red-500';
    if (env === 'QA') return 'bg-yellow-500';
    if (env === 'Development') return 'bg-green-500';
    return 'bg-gray-500';
  };

  return (
    <header className={clsx(
      "h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-20 transition-all",
      environment === 'Production' && "border-red-300 shadow-[0_0_15px_rgba(239,68,68,0.15)]"
    )}>
      {/* Left Side: Mobile Toggle & Environment */}
      <div className="flex items-center space-x-4">
        {isMobile && (
          <button onClick={toggleSidebar} className="p-2 rounded-md hover:bg-gray-100">
            <MenuIcon />
          </button>
        )}

        {/* Environment Selector */}
        <div className="relative group">
          <button className={clsx(
            "px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 transition-colors",
            envStyles[environment] || envStyles['Development']
          )}>
            <span className={`w-2 h-2 rounded-full ${getEnvColor(environment)}`}></span>
            {environment === 'Development' ? 'Dev' : environment}
            <ChevronDown size={14} />
          </button>

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 hidden group-hover:block py-1 z-50">
            {(['Development', 'QA', 'Production', 'Error403', 'Error500']).map((env) => (
              <button
                key={env}
                onClick={() => switchEnvironment(env)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <span className={`w-2 h-2 rounded-full ${getEnvColor(env)}`}></span>
                {env === 'Development' ? 'Dev' : env}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Center: Command Bar */}
      <div className="flex-1 max-w-xl mx-4 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search for user, action or workshop... (CMD+K)"
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsSearchOpen(true);
            }}
            onFocus={() => setIsSearchOpen(true)}
            onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)} // Delay for click
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">⌘K</kbd>
          </div>
        </div>

        {/* Dropdown Results */}
        {isSearchOpen && searchQuery && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50">
            <div className="p-2">
              {filteredActions.length > 0 ? (
                filteredActions.map((action, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 text-sm text-gray-700 flex items-center justify-between group"
                    onClick={() => handleActionClick(action.path)}
                  >
                    <span className="flex items-center gap-2">
                      <Command size={14} className="text-gray-400 group-hover:text-blue-500" />
                      {action.label}
                    </span>
                    <span className="text-xs text-gray-400">Jump to</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  No actions found for "{searchQuery}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right: User Profile */}
      <div className="flex items-center space-x-4">
        {/* Session Time */}
        <div className="hidden lg:flex flex-col items-end mr-2">
          <span className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider">Session</span>
          <span className="text-xs font-mono text-gray-600 font-medium">{formatSessionTime(sessionTime)}</span>
        </div>

        <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
          <Bell size={20} />
          <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
        </button>

        <div className="relative">
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="flex items-center gap-3 pl-4 border-l border-gray-200 hover:bg-gray-50 transition-colors rounded-l-none rounded-r-md p-1 focus:outline-none"
          >
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold text-gray-800">{displayName}</div>
              <div className="text-xs text-gray-500">{displayEmail}</div>
            </div>
            {user?.photoURL ? (
              <img src={user.photoURL} alt={displayName} className="h-9 w-9 rounded-full object-cover shadow-md" />
            ) : (
              <div className="h-9 w-9 bg-gradient-to-tr from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                {initial}
              </div>
            )}
            <ChevronDown size={16} className={clsx("text-gray-400 transition-transform", isProfileOpen && "rotate-180")} />
          </button>

          {/* Profile Dropdown */}
          {isProfileOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-3 border-b border-gray-100 md:hidden">
                <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
              </div>

              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Account</p>
              </div>

              <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <UserIcon size={16} className="text-gray-400" />
                Profile
              </button>

              <div className="border-t border-gray-100 my-1"></div>

              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// Simple Menu Icon for local usage
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

export default Header;
